import { create } from 'zustand';
import { api, ApiError } from './api-client.js';
import { getLocalAuthMessage } from './auth-messages.js';

export interface AccountSnapshot {
  id: string;
  pseudo: string;
  email: string | null;
  role: 'user' | 'editor' | 'admin';
  avatarUrl: string | null;
}

export interface AuthState {
  account: AccountSnapshot | null;
  csrfToken: string | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  error: string | null;
  bootstrap: () => Promise<void>;
  /**
   * Re-fetches `/api/auth/me` and refreshes the cached account (notably its
   * `role`) without disturbing `status`. Unlike `bootstrap()`, safe to call
   * repeatedly while already authenticated (e.g. after a 403, or when the
   * tab regains focus) to pick up role changes made by an admin elsewhere.
   */
  resyncAccount: () => Promise<void>;
  login: (input: { pseudo: string; password: string }) => Promise<void>;
  register: (input: { pseudo: string; password: string; email?: string }) => Promise<void>;
  logout: () => Promise<void>;
}

interface SessionResponse {
  account: AccountSnapshot;
  csrfToken: string;
}

export const useAuth = create<AuthState>((set) => ({
  account: null,
  csrfToken: null,
  status: 'idle',
  error: null,

  async bootstrap() {
    set({ status: 'loading', error: null });
    try {
      const me = await api.get<SessionResponse>('/api/auth/me');
      api.setCsrfToken(me.csrfToken);
      set({ account: me.account, csrfToken: me.csrfToken, status: 'authenticated' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ account: null, csrfToken: null, status: 'unauthenticated' });
        return;
      }
      set({
        status: 'unauthenticated',
        error: err instanceof Error ? err.message : 'Bootstrap failed',
      });
    }
  },

  async resyncAccount() {
    try {
      const me = await api.get<SessionResponse>('/api/auth/me');
      api.setCsrfToken(me.csrfToken);
      set((state) =>
        state.account?.role === me.account.role && state.account?.pseudo === me.account.pseudo
          ? { csrfToken: me.csrfToken }
          : { account: me.account, csrfToken: me.csrfToken },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ account: null, csrfToken: null, status: 'unauthenticated' });
        return;
      }
      // Transient/network error: keep the current (possibly stale) account
      // rather than disrupting the UI — the next resync attempt will retry.
    }
  },

  async login(input) {
    set({ status: 'loading', error: null });
    try {
      const session = await api.post<SessionResponse>('/api/auth/login', input);
      api.setCsrfToken(session.csrfToken);
      set({ account: session.account, csrfToken: session.csrfToken, status: 'authenticated' });
    } catch (err) {
      const message = getLocalAuthMessage(err, 'login');
      set({ status: 'unauthenticated', error: message });
      throw err;
    }
  },

  async register(input) {
    set({ status: 'loading', error: null });
    try {
      const session = await api.post<SessionResponse>('/api/auth/register', input);
      api.setCsrfToken(session.csrfToken);
      set({ account: session.account, csrfToken: session.csrfToken, status: 'authenticated' });
    } catch (err) {
      const message = getLocalAuthMessage(err, 'register');
      set({ status: 'unauthenticated', error: message });
      throw err;
    }
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } catch {
      /* best-effort */
    }
    api.setCsrfToken(null);
    set({ account: null, csrfToken: null, status: 'unauthenticated', error: null });
  },
}));

// A 403 means the server rejected a request based on the account's current
// role, but the client's cached role may be stale. Resync so permission
// checks (isAdmin/isEditor/canEdit*) reflect reality on the next render.
api.setForbiddenHandler(() => {
  void useAuth.getState().resyncAccount();
});
